import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Autocomplete,
  IconButton,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { formatDateToTR, formatTimeAgoTR, parseUTCDateToTR } from '../utils/dateUtils';
import { filterDiskUsageByAgentThresholds } from '../utils/diskUtils';
import { getRefreshCardStyles, getRefreshPaperStyles } from '../utils/refreshAnimation';
import {
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Apps as AppsIcon,
  Notifications as NotificationsIcon,
  ArrowForward as ArrowForwardIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
  Flag as FlagIcon,
  Replay as ReplayIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { TetiklemeDialog, type TetiklemeDialogInitialValues } from '../components/TetiklemeDialog';
import { useFilter } from '../contexts/FilterContext';
import { parseEStatementAlertMessage } from '../utils/parseSonIslemZamaniDates';
import { serverService } from '../services/serverService';
import { applicationDefinitionService } from '../services/applicationDefinitionService';
import { metricsService } from '../services/metricsService';
import { healthCheckService } from '../services/healthCheckService';
import { alertService } from '../services/alertService';
import { dashboardService } from '../services/dashboardService';
import { agentService } from '../services/agentService';
import { eStatementService, type SonIslemZamaniBankChartData } from '../services/eStatementService';
import { bankaService } from '../services/bankaService';
import { bankServiceRunService } from '../services/bankServiceRunService';
import type { Server } from '../types/server';
import type { Banka } from '../types/banka';
import type { ApplicationDefinition } from '../types/applicationDefinition';
import type { CurrentMetrics, DiskUsage } from '../types/metrics';
import type { Alert as AlertType } from '../types/alert';
import { AlertSeverity, AlertStatus, AlertType as AlertTypeEnum } from '../types/alert';
import type { CurrentHealthStatus } from '../types/healthCheck';
import { HealthStatus } from '../types/healthCheck';
import type { Agent } from '../types/agent';
import { AgentCapability, capabilityHelper } from '../types/agent';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface SummaryStats {
  totalServers: number;
  onlineServers: number;
  offlineServers: number;
  totalApplications: number;
  healthyApplications: number;
  unhealthyApplications: number;
  totalAlerts: number;
  activeAlerts: number;
  unresolvedAlerts: number;
  unresolvedEStatementAlerts: number;
  unresolvedNonEStatementAlerts: number;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedTenantIds, selectedServerIds, refreshTrigger, autoRefresh, isRefreshing, setSelectedServerIds, clearServerSelection } = useFilter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryStats, setSummaryStats] = useState<SummaryStats>({
    totalServers: 0,
    onlineServers: 0,
    offlineServers: 0,
    totalApplications: 0,
    healthyApplications: 0,
    unhealthyApplications: 0,
    totalAlerts: 0,
    activeAlerts: 0,
    unresolvedAlerts: 0,
    unresolvedEStatementAlerts: 0,
    unresolvedNonEStatementAlerts: 0,
  });
  const [servers, setServers] = useState<Server[]>([]);
  const [serverMetrics, setServerMetrics] = useState<Record<string, CurrentMetrics>>({});
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, CurrentHealthStatus>>({});
  const [applications, setApplications] = useState<ApplicationDefinition[]>([]);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [nonEStatementAlerts, setNonEStatementAlerts] = useState<AlertType[]>([]);
  const [eStatementAlerts, setEStatementAlerts] = useState<AlertType[]>([]);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [tetiklemeDialogOpen, setTetiklemeDialogOpen] = useState(false);
  const [tetiklemeInitialValues, setTetiklemeInitialValues] = useState<TetiklemeDialogInitialValues | undefined>(undefined);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [eStatementBankChartData, setEStatementBankChartData] = useState<SonIslemZamaniBankChartData[]>([]);
  const [bankalar, setBankalar] = useState<Banka[]>([]);
  const [sirketlerByServerId, setSirketlerByServerId] = useState<Record<string, { vkn: string; unvan: string }[]>>({});
  const [diskCarouselIndex, setDiskCarouselIndex] = useState<Record<string, number>>({});
  const [appCarouselIndex, setAppCarouselIndex] = useState<Record<string, number>>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const carouselPageSize = isMobile ? 1 : 2;
  const carouselWheelRef = useRef<Record<string, number>>({});
  const carouselDragStateRef = useRef<{
    serverId: string;
    type: 'disk' | 'app';
    startX: number;
    containerWidth: number;
    maxPage: number;
  } | null>(null);
  const [carouselDragState, setCarouselDragState] = useState<{
    serverId: string;
    type: 'disk' | 'app';
    startX: number;
    containerWidth: number;
    dragOffset: number;
    maxPage: number;
  } | null>(null);

  useEffect(() => {
    carouselDragStateRef.current = carouselDragState ? { serverId: carouselDragState.serverId, type: carouselDragState.type, startX: carouselDragState.startX, containerWidth: carouselDragState.containerWidth, maxPage: carouselDragState.maxPage } : null;
  }, [carouselDragState]);

  useEffect(() => {
    if (!carouselDragState) return;
    const onMouseMove = (ev: MouseEvent) => {
      setCarouselDragState((prev) => {
        if (!prev) return null;
        const delta = ev.clientX - prev.startX;
        const clamp = Math.min(prev.containerWidth * 0.8, 120);
        return { ...prev, dragOffset: Math.max(-clamp, Math.min(clamp, delta)) };
      });
    };
    const onMouseUp = (ev: MouseEvent) => {
      const d = carouselDragStateRef.current;
      if (!d) return;
      const delta = ev.clientX - d.startX;
      const threshold = Math.min(40, d.containerWidth * 0.15);
      if (delta < -threshold) {
        if (d.type === 'disk') setDiskCarouselIndex((p) => ({ ...p, [d.serverId]: Math.min(d.maxPage, (p[d.serverId] ?? 0) + 1) }));
        else setAppCarouselIndex((p) => ({ ...p, [d.serverId]: Math.min(d.maxPage, (p[d.serverId] ?? 0) + 1) }));
      } else if (delta > threshold) {
        if (d.type === 'disk') setDiskCarouselIndex((p) => ({ ...p, [d.serverId]: Math.max(0, (p[d.serverId] ?? 0) - 1) }));
        else setAppCarouselIndex((p) => ({ ...p, [d.serverId]: Math.max(0, (p[d.serverId] ?? 0) - 1) }));
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setCarouselDragState(null);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [carouselDragState?.serverId, carouselDragState?.type]);

  const handleCarouselMouseDown = useCallback((serverId: string, type: 'disk' | 'app', maxPage: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (maxPage <= 0) return;
    const el = e.currentTarget as HTMLElement;
    setCarouselDragState({
      serverId,
      type,
      startX: e.clientX,
      containerWidth: el.offsetWidth,
      dragOffset: 0,
      maxPage,
    });
  }, []);

  const handleCarouselWheel = useCallback((serverId: string, type: 'disk' | 'app', maxPage: number, e: React.WheelEvent) => {
    if (maxPage <= 0) return;
    e.preventDefault();
    const key = `${serverId}_${type}`;
    if (Date.now() - (carouselWheelRef.current[key] ?? 0) < 300) return;
    carouselWheelRef.current[key] = Date.now();
    if (e.deltaY > 0) {
      if (type === 'disk') setDiskCarouselIndex((p) => ({ ...p, [serverId]: Math.min(maxPage, (p[serverId] ?? 0) + 1) }));
      else setAppCarouselIndex((p) => ({ ...p, [serverId]: Math.min(maxPage, (p[serverId] ?? 0) + 1) }));
    } else {
      if (type === 'disk') setDiskCarouselIndex((p) => ({ ...p, [serverId]: Math.max(0, (p[serverId] ?? 0) - 1) }));
      else setAppCarouselIndex((p) => ({ ...p, [serverId]: Math.max(0, (p[serverId] ?? 0) - 1) }));
    }
  }, []);
  const [loadingEStatementChart, setLoadingEStatementChart] = useState(false);
  // Backend'den gelen server durumları (ServerSummary)
  const [serverStatuses, setServerStatuses] = useState<Record<string, { status: string; reason?: string; reasonDetails?: string; agentIsOnline: boolean }>>({});
  // Durum filtresi: çoklu seçim (Sağlıklı, Uyarı, Kritik). Varsayılan: Uyarı ve Kritik.
  const [applicationFilter, setApplicationFilter] = useState<{
    search: string;
    status: Array<'healthy' | 'warning' | 'critical'>;
    type: 'all' | string;
    server: 'all' | string;
  }>({
    search: '',
    status: ['warning', 'critical'],
    type: 'all',
    server: 'all',
  });
  const [unhealthyApplications, setUnhealthyApplications] = useState<Array<{ application: ApplicationDefinition; currentStatus: CurrentHealthStatus }>>([]);
  const [applicationSort, setApplicationSort] = useState<{
    field: 'serverName' | 'name' | 'status' | 'lastCheck';
    direction: 'asc' | 'desc';
  }>({
    field: 'lastCheck',
    direction: 'desc',
  });

  const selectedTenantIdsKey = selectedTenantIds.join(',');
  const selectedServerIdsKey = selectedServerIds.join(',');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Server'ları yükle (sadece aktif server'lar)
      let allServers = await serverService.getAll();
      
      // Önce aktif olmayan server'ları filtrele
      allServers = allServers.filter((s) => s.isActive);
      
      // Filtreleme
      if (selectedTenantIds.length > 0) {
        allServers = allServers.filter((s) => selectedTenantIds.includes(s.tenantId));
      }
      if (selectedServerIds.length > 0) {
        allServers = allServers.filter((s) => selectedServerIds.includes(s.id));
      }

      setServers(allServers);

      // Agent'ları yükle (sadece aktif olanlar; pasif agent'lar dashboard'da gösterilmez)
      const allAgents = await agentService.getAll();
      setAgents(allAgents.filter((a) => a.isActive));

      // Application Definition'ları yükle
      const tenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
      const serverId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
      let applications = await applicationDefinitionService.getAll(tenantId, serverId);
      
      // Sadece aktif server'lardaki aktif application'ları filtrele
      const activeServerIds = new Set(allServers.map(s => s.id));
      applications = applications.filter((app) => app.isActive && activeServerIds.has(app.serverId));

      // Her server için metrics ve her application için health status yükle
      const metricsData: Record<string, CurrentMetrics> = {};
      const statusesData: Record<string, CurrentHealthStatus> = {};

      await Promise.all([
        ...allServers.map(async (server) => {
          try {
            const metrics = await metricsService.getCurrentMetrics(server.id);
            if (metrics) {
              metricsData[server.id] = metrics;
            }
          } catch {
            // Metrics yoksa atla
          }
        }),
        ...applications.map(async (app) => {
          try {
            const status = await healthCheckService.getCurrentStatus(app.id);
            if (status) {
              statusesData[app.id] = status;
            }
          } catch {
            // Status yoksa atla
          }
        }),
      ]);

      setServerMetrics(metricsData);
      setApplicationStatuses(statusesData);
      setApplications(applications);

      // Uyarı/Kritik son 10 uygulama (Dashboard kartı için)
      try {
        const unhealthy = await applicationDefinitionService.getUnhealthy(10);
        setUnhealthyApplications(unhealthy);
        setApplicationStatuses((prev) => ({
          ...prev,
          ...Object.fromEntries(unhealthy.map((u) => [u.application.id, u.currentStatus])),
        }));
      } catch {
        setUnhealthyApplications([]);
      }

      // Backend'den server durumlarını al (her tenant için)
      const serverStatusesMap: Record<string, { status: string; reason?: string; reasonDetails?: string; agentIsOnline: boolean }> = {};
      
      if (selectedTenantIds.length > 0) {
        // Seçili tenant'lar için backend'den durum al
        await Promise.all(
          selectedTenantIds.map(async (tid) => {
            try {
              const tenantDashboard = await dashboardService.getTenantDashboard(tid);
              tenantDashboard.servers.forEach((server) => {
                serverStatusesMap[server.id] = {
                  status: server.status,
                  reason: server.reason,
                  reasonDetails: server.reasonDetails,
                  agentIsOnline: server.agentIsOnline,
                };
              });
            } catch (err: any) {
              console.error(`[Dashboard] Tenant ${tid} dashboard yüklenirken hata:`, err);
            }
          })
        );
      } else {
        // Hiç tenant seçilmemişse, tüm tenant'lar için durum al
        // Önce tenant'ları bul
        const uniqueTenantIds = Array.from(new Set(allServers.map(s => s.tenantId)));
        await Promise.all(
          uniqueTenantIds.map(async (tid) => {
            try {
              const tenantDashboard = await dashboardService.getTenantDashboard(tid);
              tenantDashboard.servers.forEach((server) => {
                serverStatusesMap[server.id] = {
                  status: server.status,
                  reason: server.reason,
                  reasonDetails: server.reasonDetails,
                  agentIsOnline: server.agentIsOnline,
                };
              });
            } catch (err: any) {
              console.error(`[Dashboard] Tenant ${tid} dashboard yüklenirken hata:`, err);
            }
          })
        );
      }
      
      setServerStatuses(serverStatusesMap);

      // Alert'leri yükle (EStatement hariç ve EStatement olanlar ayrı ayrı - backend'de filtreleme)
      try {
        const activeServerIds = new Set(allServers.map(s => s.id));
        
        // EStatement olmayan alert'leri yükle (backend'de filtreleme ile)
        // EStatement, CpuUsage, RamUsage, DiskUsage hariç
        const nonEStatementAlertsResult = await alertService.getAll({
          tenantId,
          serverId: undefined,
          excludeAlertTypes: [
            AlertTypeEnum.EStatement,
            AlertTypeEnum.CpuUsage,
            AlertTypeEnum.RamUsage,
            AlertTypeEnum.DiskUsage
          ],
          status: AlertStatus.New,
          page: 1,
          pageSize: 10,
        });
        
        const eStatementAlertsResult = await alertService.getAll({
          tenantId,
          serverId: undefined,
          alertType: AlertTypeEnum.EStatement,
          status: AlertStatus.New,
          page: 1,
          pageSize: 10,
        });
        
        // Sadece aktif server'lardaki alert'leri filtrele (frontend'de)
        const nonEStatementAlerts = (nonEStatementAlertsResult.items || []).filter(
          a => (!a.serverId || activeServerIds.has(a.serverId))
        );
        
        const eStatementAlerts = (eStatementAlertsResult.items || []).filter(
          a => (!a.serverId || activeServerIds.has(a.serverId))
        );
        
        // Tüm alert'leri birleştir (geriye dönük uyumluluk için)
        setAlerts([...nonEStatementAlerts, ...eStatementAlerts]);
        setNonEStatementAlerts(nonEStatementAlerts);
        setEStatementAlerts(eStatementAlerts);
      } catch (err: any) {
        console.error('[Dashboard] Alert\'ler yüklenirken hata:', err);
        setAlerts([]);
        setNonEStatementAlerts([]);
        setEStatementAlerts([]);
      }

      // eStatement chart verilerini yükle
      try {
        setLoadingEStatementChart(true);
        const tenantId = selectedTenantIds.length > 0 ? selectedTenantIds[0] : undefined;
        const serverId = selectedServerIds.length > 0 ? selectedServerIds[0] : undefined;

        const chartResult = await eStatementService.getChart(
          tenantId,
          serverId
        );
        setEStatementBankChartData(chartResult.bankChartData);
      } catch (err: any) {
        console.error('[Dashboard] eStatement chart verileri yüklenirken hata:', err);
        setEStatementBankChartData([]);
      } finally {
        setLoadingEStatementChart(false);
      }

      // Özet istatistikleri backend'den al (her zaman backend kullanılır)
      try {
        const summary = await dashboardService.getSummary({
          tenantIds: selectedTenantIds.length > 0 ? selectedTenantIds : undefined,
          serverIds: selectedServerIds.length > 0 ? selectedServerIds : undefined,
        });
        setSummaryStats({
          totalServers: summary.totalServers,
          onlineServers: summary.onlineServers,
          offlineServers: summary.offlineServers,
          totalApplications: summary.totalApplications,
          healthyApplications: summary.healthyApplications,
          unhealthyApplications: summary.unhealthyApplications,
          totalAlerts: summary.totalAlerts,
          activeAlerts: summary.activeAlerts,
          unresolvedAlerts: summary.unresolvedAlerts,
          unresolvedEStatementAlerts: summary.unresolvedEStatementAlerts ?? 0,
          unresolvedNonEStatementAlerts: summary.unresolvedNonEStatementAlerts ?? 0,
        });
      } catch (err: any) {
        console.error('[Dashboard] Summary yüklenirken hata:', err);
        // Hata durumunda boş değerler set et
        setSummaryStats({
          totalServers: 0,
          onlineServers: 0,
          offlineServers: 0,
          totalApplications: 0,
          healthyApplications: 0,
          unhealthyApplications: 0,
          totalAlerts: 0,
          activeAlerts: 0,
          unresolvedAlerts: 0,
          unresolvedEStatementAlerts: 0,
          unresolvedNonEStatementAlerts: 0,
        });
      }
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantIdsKey, selectedServerIdsKey]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await bankaService.getAll();
        setBankalar(data);
      } catch {
        setBankalar([]);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (eStatementAlerts.length === 0) {
      setSirketlerByServerId({});
      return;
    }
    const serverIds = Array.from(new Set(eStatementAlerts.map((a) => a.serverId).filter((id): id is string => Boolean(id))));
    let cancelled = false;
    const load = async () => {
      const next: Record<string, { vkn: string; unvan: string }[]> = {};
      await Promise.all(
        serverIds.map(async (serverId) => {
          try {
            const list = await bankServiceRunService.getSirketler(serverId);
            if (!cancelled) next[serverId] = list.map((s) => ({ vkn: s.vkn, unvan: s.unvan }));
          } catch {
            if (!cancelled) next[serverId] = [];
          }
        })
      );
      if (!cancelled) setSirketlerByServerId(next);
    };
    load();
    return () => { cancelled = true; };
  }, [eStatementAlerts]);

  const getServerHealthStatus = (server: Server): {
    status: 'healthy' | 'warning' | 'critical' | 'unknown';
    reason?: string;
    reasonDetails?: string;
  } => {
    // A. Erişim Kontrolü (En Üst Öncelik)
    
    // ÖNCE AgentHeartbeat Alert Kontrolü (en güvenilir kaynak - agent gerçekten offline ise alert oluşur)
    const agentHeartbeatAlerts = alerts.filter(a => 
      a.serverId === server.id && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    
    if (agentHeartbeatAlerts.length > 0) {
      // AgentHeartbeat alert varsa → critical (agent offline)
      // Bu durumda diğer alert'lere ve metrics'e bakılmamalı
      const latestAlert = agentHeartbeatAlerts.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
      return {
        status: 'critical',
        reason: 'AgentHeartbeat Alert',
        reasonDetails: `Agent heartbeat timeout. ${latestAlert.message}`
      };
    }
    
    // AgentHeartbeat alert yoksa, agent durumunu kontrol et
    
    // 1. Agent Durumu Kontrolü
    const serverAgents = agents.filter(a => a.serverId === server.id);
    if (serverAgents.length === 0) {
      // Agent yoksa → unknown
      return {
        status: 'unknown',
        reason: 'Agent Yok',
        reasonDetails: 'Server için aktif agent bulunamadı.'
      };
    }
    
    const hasOnlineAgent = serverAgents.some(a => a.isOnline);
    if (!hasOnlineAgent) {
      // Agent var ama offline → critical (erişim yok)
      return {
        status: 'critical',
        reason: 'Agent Offline',
        reasonDetails: 'Agent\'a erişim yok. Agent offline durumda.'
      };
    }
    
    // B. Erişim Varsa Alert Kontrolü
    
    // 2. Çözülmemiş alert'leri kontrol et - en son tarihli alert'e göre server durumunu belirle
    const otherAlerts = alerts.filter(a => 
      a.serverId === server.id && 
      a.status !== AlertStatus.Resolved &&
      a.alertType !== AlertTypeEnum.AgentHeartbeat
    );
    
    if (otherAlerts.length > 0) {
      // En kritik severity'li alert'i bul (Critical > High > Medium > Low)
      // Severity'ye göre sırala, aynı severity'de ise en son tarihli olanı al
      const mostCriticalAlert = otherAlerts.sort((a, b) => {
        // Önce severity'ye göre sırala (Critical > High > Medium > Low)
        const severityOrder = { [AlertSeverity.Critical]: 4, [AlertSeverity.High]: 3, [AlertSeverity.Medium]: 2, [AlertSeverity.Low]: 1 };
        const severityDiff = (severityOrder[b.severity] || 0) - (severityOrder[a.severity] || 0);
        if (severityDiff !== 0) return severityDiff;
        
        // Aynı severity'de ise en son tarihli olanı al
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // En yeni önce
      })[0];
      
      // En kritik alert'in severity'sine göre durum belirle
      const alertStatus = mostCriticalAlert.severity === AlertSeverity.Critical || mostCriticalAlert.severity === AlertSeverity.High
        ? 'critical'
        : 'warning';
      
      return {
        status: alertStatus,
        reason: `${mostCriticalAlert.alertType} Alert`,
        reasonDetails: `${mostCriticalAlert.alertType} alert: ${mostCriticalAlert.message}`
      };
    }
    
    // C. Alert Yoksa Metrics Kontrolü
    
    // 3. Metrics'e göre değerlendir (sadece agent online ve alert yoksa)
    const metrics = serverMetrics[server.id];
    if (!metrics) {
      return {
        status: 'warning',
        reason: 'Metrics Verisi Yok',
        reasonDetails: 'Server için metrik verisi bulunamadı.'
      };
    }
    
    // Server'ın agent'ını bul (threshold'lar için)
    const serverAgent = serverAgents[0];
    const caps = serverAgent?.capabilities;
    const hasCpu = capabilityHelper.hasCapability(caps, AgentCapability.CpuMonitoring);
    const hasRam = capabilityHelper.hasCapability(caps, AgentCapability.MemoryMonitoring);
    const hasDisk = capabilityHelper.hasCapability(caps, AgentCapability.DiskMonitoring);

    // Agent threshold'larını al (varsayılan değerler fallback)
    const cpuWarningThreshold = serverAgent?.cpuWarningThreshold ?? 80;
    const cpuCriticalThreshold = serverAgent?.cpuCriticalThreshold ?? 90;
    const ramWarningThreshold = serverAgent?.ramWarningThreshold ?? 80;
    const ramCriticalThreshold = serverAgent?.ramCriticalThreshold ?? 90;

    // Disk kullanımını parse et ve maksimum kullanımı bul (backend ile aynı mantık) - sadece disk izleme açıksa
    let maxDiskUsage = 0;
    let maxDiskDrive: string | null = null;
    let diskWarningThreshold = 80;
    let diskCriticalThreshold = 90;
    
    if (hasDisk && metrics.latestDiskUsage) {
      const diskUsageRaw = parseDiskUsage(metrics.latestDiskUsage);
      const diskUsage = filterDiskUsageByAgentThresholds(diskUsageRaw, serverAgent?.diskThresholds);
      // Disk threshold'larını parse et
      if (serverAgent?.diskThresholds) {
        try {
          const diskThresholds = JSON.parse(serverAgent.diskThresholds);
          if (Array.isArray(diskThresholds) && diskThresholds.length > 0) {
            // Her disk için kendi threshold'unu kullan (backend ile aynı mantık)
            Object.entries(diskUsage).forEach(([drive, usage]) => {
              // Drive'ı normalize et (C: veya C formatında olabilir)
              const normalizedDrive = drive.trim().toUpperCase();
              const normalizedDriveWithColon = normalizedDrive.endsWith(':') ? normalizedDrive : normalizedDrive + ':';
              
              // Bu disk için threshold bul
              const diskConfig = diskThresholds.find((d: any) => {
                const normalized = (d.drive?.trim().toUpperCase() || '').endsWith(':') 
                  ? d.drive?.trim().toUpperCase() 
                  : d.drive?.trim().toUpperCase() + ':';
                return normalized === normalizedDriveWithColon;
              });
              
              if (diskConfig) {
                // Bu disk için threshold'ları kullan
                const driveWarningThreshold = diskConfig.warning || 80;
                const driveCriticalThreshold = diskConfig.critical || 90;
                
                // En yüksek kullanıma sahip disk'i bul (backend ile aynı mantık)
                if (usage > maxDiskUsage) {
                  maxDiskUsage = usage;
                  maxDiskDrive = drive;
                  diskWarningThreshold = driveWarningThreshold;
                  diskCriticalThreshold = driveCriticalThreshold;
                }
              }
              // Eğer disk threshold'ları varsa ama bu disk için threshold yoksa, atla (backend ile aynı mantık)
            });
          } else {
            // Disk threshold yoksa tüm diskleri kontrol et
            Object.entries(diskUsage).forEach(([drive, usage]) => {
              if (usage > maxDiskUsage) {
                maxDiskUsage = usage;
                maxDiskDrive = drive;
              }
            });
          }
        } catch {
          // JSON parse hatası - varsayılan değerleri kullan
          Object.entries(diskUsage).forEach(([drive, usage]) => {
            if (usage > maxDiskUsage) {
              maxDiskUsage = usage;
              maxDiskDrive = drive;
            }
          });
        }
      } else {
        // Agent'ta disk threshold yoksa tüm diskleri kontrol et
        Object.entries(diskUsage).forEach(([drive, usage]) => {
          if (usage > maxDiskUsage) {
            maxDiskUsage = usage;
            maxDiskDrive = drive;
          }
        });
      }
    }
    
    // Durum hesaplama: Critical > Warning > Healthy (sadece açık olan özellikler dikkate alınır)
    const isCritical = (hasCpu && metrics.latestCpuUsage >= cpuCriticalThreshold)
      || (hasRam && metrics.latestRamUsagePercent >= ramCriticalThreshold)
      || (hasDisk && maxDiskUsage >= diskCriticalThreshold);
    const isWarning = (hasCpu && metrics.latestCpuUsage >= cpuWarningThreshold)
      || (hasRam && metrics.latestRamUsagePercent >= ramWarningThreshold)
      || (hasDisk && maxDiskUsage >= diskWarningThreshold);

    if (isCritical) {
      if (hasCpu && metrics.latestCpuUsage >= cpuCriticalThreshold) {
        return {
          status: 'critical',
          reason: 'CPU Critical Threshold Aşıldı',
          reasonDetails: `CPU kullanımı kritik seviyede: %${metrics.latestCpuUsage.toFixed(1)} >= %${cpuCriticalThreshold.toFixed(1)}`
        };
      }
      if (hasRam && metrics.latestRamUsagePercent >= ramCriticalThreshold) {
        return {
          status: 'critical',
          reason: 'RAM Critical Threshold Aşıldı',
          reasonDetails: `RAM kullanımı kritik seviyede: %${metrics.latestRamUsagePercent.toFixed(1)} >= %${ramCriticalThreshold.toFixed(1)}`
        };
      }
      if (hasDisk && maxDiskUsage >= diskCriticalThreshold) {
        return {
          status: 'critical',
          reason: 'Disk Critical Threshold Aşıldı',
          reasonDetails: `${maxDiskDrive || 'Disk'} sürücüsü kritik seviyede dolu: %${maxDiskUsage.toFixed(1)} >= %${diskCriticalThreshold.toFixed(1)}`
        };
      }
    }
    if (isWarning) {
      if (hasCpu && metrics.latestCpuUsage >= cpuWarningThreshold) {
        return {
          status: 'warning',
          reason: 'CPU Warning Threshold Aşıldı',
          reasonDetails: `CPU kullanımı yüksek: %${metrics.latestCpuUsage.toFixed(1)} >= %${cpuWarningThreshold.toFixed(1)}`
        };
      }
      if (hasRam && metrics.latestRamUsagePercent >= ramWarningThreshold) {
        return {
          status: 'warning',
          reason: 'RAM Warning Threshold Aşıldı',
          reasonDetails: `RAM kullanımı yüksek: %${metrics.latestRamUsagePercent.toFixed(1)} >= %${ramWarningThreshold.toFixed(1)}`
        };
      }
      if (hasDisk && maxDiskUsage >= diskWarningThreshold) {
        return {
          status: 'warning',
          reason: 'Disk Warning Threshold Aşıldı',
          reasonDetails: `${maxDiskDrive || 'Disk'} sürücüsü yüksek seviyede dolu: %${maxDiskUsage.toFixed(1)} >= %${diskWarningThreshold.toFixed(1)}`
        };
      }
    }
    
    return {
      status: 'healthy',
      reason: 'Sağlıklı',
      reasonDetails: 'Tüm metrikler normal seviyede.'
    };
  };

  const getServerApplicationStats = (serverId: string) => {
    const serverApps = applications.filter((app) => app.serverId === serverId);
    let healthy = 0;
    let warning = 0;
    let critical = 0;

    // Server'ın agent durumunu kontrol et
    const serverAgents = agents.filter(a => a.serverId === serverId);
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    
    // Server'ın AgentHeartbeat alert'ini kontrol et
    const serverAlerts = alerts.filter(a => 
      a.serverId === serverId && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    const hasAgentHeartbeatAlert = serverAlerts.length > 0;
    
    // Application'lar için alert'leri grupla
    const applicationAlertsMap: Record<string, AlertType[]> = {};
    alerts.forEach((alert) => {
      if (alert.applicationDefinitionId && alert.status !== AlertStatus.Resolved) {
        if (!applicationAlertsMap[alert.applicationDefinitionId]) {
          applicationAlertsMap[alert.applicationDefinitionId] = [];
        }
        applicationAlertsMap[alert.applicationDefinitionId].push(alert);
      }
    });

    serverApps.forEach((app) => {
      // A. Erişim Kontrolü (En Üst Öncelik)
      
      // 1. Server Agent Durumu
      if (!hasOnlineAgent && serverAgents.length > 0) {
        // Server'ın agent'ı var ama offline - uygulama unknown (erişim yok)
        warning++;
        return;
      }
      
      // 2. AgentHeartbeat Alert Kontrolü
      if (hasAgentHeartbeatAlert) {
        // AgentHeartbeat alert varsa - uygulama unknown (erişim yok)
        warning++;
        return;
      }
      
      // B. Erişim Varsa Health Check ve Alert Birlikte Değerlendirme
      
      // Health Check durumu
      const status = applicationStatuses[app.id];
      const isHealthCheckUnhealthy = status?.latestHealthStatus === HealthStatus.Unhealthy;
      const isHealthCheckHealthy = status?.latestHealthStatus === HealthStatus.Healthy;
      
      // Application'a ait en son tarihli alert'i al
      const appAlerts = applicationAlertsMap[app.id] || [];
      const latestAppAlert = appAlerts.length > 0 
        ? appAlerts.sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA; // En yeni önce
          })[0]
        : null;
      
      // Alert durumu
      const hasApplicationUnhealthyAlert = latestAppAlert?.alertType === AlertTypeEnum.ApplicationUnhealthy;
      const hasOtherAlert = latestAppAlert && latestAppAlert.alertType !== AlertTypeEnum.ApplicationUnhealthy;
      
      // Sonuç Mantığı:
      // Health Check Unhealthy VEYA Alert var (ApplicationUnhealthy veya yüksek severity) → unhealthy
      // Health Check Healthy VE Alert yok → healthy
      // Health Check Unknown veya yok VE Alert yok → unknown
      
      if (isHealthCheckUnhealthy || hasApplicationUnhealthyAlert) {
        // Health Check Unhealthy VEYA ApplicationUnhealthy alert var → unhealthy
        critical++;
      } else if (hasOtherAlert) {
        // Diğer alert tipleri varsa → Severity'ye göre durum belirle
        const alertStatus = latestAppAlert!.severity === AlertSeverity.Critical || 
                           latestAppAlert!.severity === AlertSeverity.High ||
                           latestAppAlert!.severity === AlertSeverity.Medium
          ? 'unhealthy'
          : 'unknown';
        
        if (alertStatus === 'unhealthy') {
          critical++;
        } else {
          warning++;
        }
      } else if (isHealthCheckHealthy) {
        // Health Check Healthy VE Alert yok → healthy
        healthy++;
      } else {
        // Health Check Unknown veya yok VE Alert yok → unknown
        warning++;
      }
    });

    return { total: serverApps.length, healthy, warning, critical };
  };

  // Disk kullanımını parse et
  const parseDiskUsage = (diskUsageJson: string | null | undefined): DiskUsage => {
    if (diskUsageJson == null || diskUsageJson === '') return {};
    try {
      const parsed = JSON.parse(diskUsageJson);
      if (parsed == null) return {};

      // Array formatı: [{ "Drive": "C:", "UsagePercent": 75.5 }, ...] veya [{ "Device": "C:", "UsagePercent": 75.5 }, ...]
      if (Array.isArray(parsed)) {
        const result: DiskUsage = {};
        parsed.forEach((item: any) => {
          const driveName = item.Drive || item.Device;
          if (driveName && item.UsagePercent !== undefined) {
            result[driveName] = typeof item.UsagePercent === 'number' 
              ? item.UsagePercent 
              : parseFloat(item.UsagePercent) || 0;
          }
        });
        return result;
      }
      
      // Dictionary formatı: { "C:": 75, "D:": 45 }
      if (typeof parsed === 'object' && parsed !== null) {
        const result: DiskUsage = {};
        Object.entries(parsed).forEach(([key, value]) => {
          result[key] = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
        });
        return result;
      }
      
      return {};
    } catch {
      return {};
    }
  };

  // formatTimeAgo artık dateUtils'ten geliyor (formatTimeAgoTR)

  const handleServerClick = (serverId: string) => {
    navigate(`/metrics/servers/${serverId}`);
  };

  const handleApplicationRowClick = (applicationId: string) => {
    navigate(`/application-definitions/${applicationId}/history`);
  };

  const handleApplicationSort = (field: 'serverName' | 'name' | 'status' | 'lastCheck') => {
    setApplicationSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Uygulama durumu: healthy | warning | critical (agent offline = critical, yoksa health status'a göre)
  const getApplicationStatusForFilter = (app: ApplicationDefinition): 'healthy' | 'warning' | 'critical' => {
    const serverAgents = agents.filter(a => a.serverId === app.serverId);
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    if (!hasOnlineAgent && serverAgents.length > 0) return 'critical';
    const status = applicationStatuses[app.id];
    if (!status) return 'warning';
    if (status.latestHealthStatus === HealthStatus.Healthy) return 'healthy';
    if (status.latestHealthStatus === HealthStatus.Unhealthy) return 'critical';
    return 'warning';
  };

  // Filtrelenmiş ve sıralanmış uygulamalar
  const getFilteredAndSortedApplications = () => {
    let filtered: ApplicationDefinition[];

    const statusFilter = applicationFilter.status;
    if (statusFilter.length === 0) {
      filtered = [...applications];
    } else {
      filtered = applications.filter((app) => {
        const appStatus = getApplicationStatusForFilter(app);
        return statusFilter.includes(appStatus);
      });
    }

    // Arama, tip ve server filtreleri (tüm durumlar için)
    filtered = filtered.filter((app) => {
      if (
        applicationFilter.search &&
        !app.name.toLowerCase().includes(applicationFilter.search.toLowerCase()) &&
        !app.serverName.toLowerCase().includes(applicationFilter.search.toLowerCase())
      ) return false;
      if (applicationFilter.type !== 'all' && app.applicationTypeName !== applicationFilter.type) return false;
      if (applicationFilter.server !== 'all' && app.serverId !== applicationFilter.server) return false;
      return true;
    });

    // Sıralama
    filtered.sort((a, b) => {
      const statusA = applicationStatuses[a.id];
      const statusB = applicationStatuses[b.id];

      switch (applicationSort.field) {
        case 'serverName':
          return applicationSort.direction === 'asc'
            ? a.serverName.localeCompare(b.serverName)
            : b.serverName.localeCompare(a.serverName);
        case 'name':
          return applicationSort.direction === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        case 'status':
          // Agent durumunu kontrol et
          const serverAgentsA = agents.filter(agent => agent.serverId === a.serverId);
          const hasOnlineAgentA = serverAgentsA.length > 0 && serverAgentsA.some(agent => agent.isOnline);
          const serverAgentsB = agents.filter(agent => agent.serverId === b.serverId);
          const hasOnlineAgentB = serverAgentsB.length > 0 && serverAgentsB.some(agent => agent.isOnline);
          
          // Eğer agent offline ise, uygulama Unhealthy (kritik) olarak kabul edilir
          let statusValueA: HealthStatus;
          if (!hasOnlineAgentA && serverAgentsA.length > 0) {
            statusValueA = HealthStatus.Unhealthy; // Kritik olarak sırala
          } else {
            statusValueA = statusA?.latestHealthStatus ?? HealthStatus.Unknown;
          }
          
          let statusValueB: HealthStatus;
          if (!hasOnlineAgentB && serverAgentsB.length > 0) {
            statusValueB = HealthStatus.Unhealthy; // Kritik olarak sırala
          } else {
            statusValueB = statusB?.latestHealthStatus ?? HealthStatus.Unknown;
          }
          
          return applicationSort.direction === 'asc'
            ? statusValueA - statusValueB
            : statusValueB - statusValueA;
        case 'lastCheck':
          const timeA = statusA?.latestCheckTime
            ? parseUTCDateToTR(statusA.latestCheckTime).getTime()
            : 0;
          const timeB = statusB?.latestCheckTime
            ? parseUTCDateToTR(statusB.latestCheckTime).getTime()
            : 0;
          return applicationSort.direction === 'asc' ? timeA - timeB : timeB - timeA;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredApplications = getFilteredAndSortedApplications();

  const getApplicationStatusDisplay = (app: ApplicationDefinition): {
    label: string;
    color: 'success' | 'error' | 'warning' | 'default';
    icon: React.ReactElement;
    time: string | null;
    reason?: string;
    reasonDetails?: string;
  } => {
    // A. Erişim Kontrolü (En Üst Öncelik)
    
    // 1. Server Agent Durumu
    const serverAgents = agents.filter(a => a.serverId === app.serverId);
    const hasOnlineAgent = serverAgents.length > 0 && serverAgents.some(a => a.isOnline);
    
    if (!hasOnlineAgent && serverAgents.length > 0) {
      // Server'ın agent'ı var ama offline - unknown (erişim yok)
      return {
        label: 'Bilinmiyor (Erişim Yok)',
        color: 'default' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
        time: null,
        reason: 'Agent Offline',
        reasonDetails: 'Server agent offline durumda. Application durumu kontrol edilemiyor.'
      };
    }
    
    // 2. AgentHeartbeat Alert Kontrolü
    const serverAlerts = alerts.filter(a => 
      a.serverId === app.serverId && 
      a.status !== AlertStatus.Resolved &&
      a.alertType === AlertTypeEnum.AgentHeartbeat
    );
    
    if (serverAlerts.length > 0) {
      // AgentHeartbeat alert varsa - unknown (erişim yok)
      const latestAlert = serverAlerts.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
      return {
        label: 'Bilinmiyor (Erişim Yok)',
        color: 'default' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
        time: null,
        reason: 'AgentHeartbeat Alert',
        reasonDetails: `Server agent offline. ${latestAlert.message}`
      };
    }
    
    // B. Erişim Varsa Backend'den Gelen Status'u Kullan
    // Backend zaten Health Check ve Alert'i birlikte değerlendiriyor
    const status = applicationStatuses[app.id];
    if (!status) {
      return {
        label: 'Bilinmiyor',
        color: 'warning' as const,
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
        time: null,
        reason: 'Health Check Verisi Yok',
        reasonDetails: 'Application için health check verisi bulunamadı.'
      };
    }

    // Application'a ait alert'leri kontrol et
    const appAlerts = alerts.filter(a => 
      a.applicationDefinitionId === app.id && 
      a.status !== AlertStatus.Resolved
    );
    const latestAppAlert = appAlerts.length > 0 
      ? appAlerts.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]
      : null;

    switch (status.latestHealthStatus) {
      case HealthStatus.Healthy:
        return {
          label: 'Sağlıklı',
          color: 'success' as const,
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          time: status.latestCheckTime,
          reason: 'Sağlıklı',
          reasonDetails: status.latestCheckTime 
            ? `Health check sonucu: Healthy. Son kontrol: ${formatTimeAgoTR(status.latestCheckTime)}`
            : 'Health check sonucu: Healthy'
        };
      case HealthStatus.Unhealthy:
        const unhealthyReason = latestAppAlert?.alertType === AlertTypeEnum.ApplicationUnhealthy
          ? 'ApplicationUnhealthy Alert'
          : 'Health Check Unhealthy';
        const unhealthyDetails = latestAppAlert?.alertType === AlertTypeEnum.ApplicationUnhealthy
          ? `Application unhealthy alert: ${latestAppAlert.message}`
          : status.latestCheckTime
            ? `Health check sonucu: Unhealthy. Son kontrol: ${formatTimeAgoTR(status.latestCheckTime)}`
            : 'Health check sonucu: Unhealthy';
        return {
          label: 'Sağlıksız',
          color: 'error' as const,
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          time: status.latestCheckTime,
          reason: unhealthyReason,
          reasonDetails: unhealthyDetails
        };
      case HealthStatus.Unknown:
        const unknownReason = latestAppAlert
          ? `${latestAppAlert.alertType} Alert`
          : 'Health Check Verisi Yok';
        const unknownDetails = latestAppAlert
          ? `${latestAppAlert.alertType} alert: ${latestAppAlert.message}`
          : 'Application için health check verisi bulunamadı.';
        return {
          label: 'Bilinmiyor',
          color: 'warning' as const,
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
          time: status.latestCheckTime,
          reason: unknownReason,
          reasonDetails: unknownDetails
        };
      default:
        return {
          label: 'Bilinmiyor',
          color: 'warning' as const,
          icon: <WarningIcon sx={{ fontSize: 16 }} />,
          time: null,
          reason: 'Bilinmeyen Durum',
          reasonDetails: 'Application durumu belirlenemedi.'
        };
    }
  };

  if (loading && summaryStats.totalServers === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box mb={4}>
        <Typography variant="h4" component="h1" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sistem durumunu ve metrikleri görüntüleyin
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Özet Kartlar – light tema, ikon buton ile detay */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 4 }}>
        {/* Sunucular */}
        <Card
          onClick={() => { clearServerSelection(); navigate('/servers'); }}
          sx={{
            cursor: 'pointer',
            minHeight: 160,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(16,185,129,0.15)',
            },
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            ...getRefreshCardStyles(isRefreshing),
          }}
        >
          <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : 'success.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ComputerIcon sx={{ fontSize: 22, color: 'success.main' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">Sunucular</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip label="Online" size="small" sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, fontSize: '0.7rem' }} />
                <Tooltip title="Detayları gör">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); clearServerSelection(); navigate('/servers'); }}
                    sx={{ color: 'success.main', '&:hover': { bgcolor: 'success.50' } }}
                    aria-label="Sunucu detayları"
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ flex: 1, py: 1.5, px: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
              <Typography variant="caption" color="text.secondary">Çevrimiçi / Toplam</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, letterSpacing: '-0.02em' }}>
                {summaryStats.onlineServers} <Typography component="span" variant="h5" fontWeight={500} color="text.secondary">/ {summaryStats.totalServers}</Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
        {/* Uygulamalar */}
        <Card
          onClick={() => { clearServerSelection(); navigate('/application-definitions'); }}
          sx={{
            cursor: 'pointer',
            minHeight: 160,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(99,102,241,0.15)',
            },
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            ...getRefreshCardStyles(isRefreshing),
          }}
        >
          <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AppsIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">Uygulamalar</Typography>
              </Box>
              <Tooltip title="Detayları gör">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); clearServerSelection(); navigate('/application-definitions'); }}
                  sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                  aria-label="Uygulama detayları"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, py: 1.5, px: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
              <Typography variant="caption" color="text.secondary">Sağlıklı / Toplam</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, letterSpacing: '-0.02em' }}>
                {summaryStats.healthyApplications} <Typography component="span" variant="h5" fontWeight={500} color="text.secondary">/ {summaryStats.totalApplications}</Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
        {/* Açık uyarılar */}
        <Card
          onClick={() => navigate('/alerts?status=unresolved')}
          sx={{
            cursor: 'pointer',
            minHeight: 160,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: summaryStats.unresolvedAlerts > 0
                ? '0 12px 24px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(239,68,68,0.2)'
                : '0 12px 24px -8px rgba(0,0,0,0.12)',
            },
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            ...getRefreshCardStyles(isRefreshing),
          }}
        >
          <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : (summaryStats.unresolvedAlerts > 0 ? 'error.50' : 'grey.100'), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <NotificationsIcon sx={{ fontSize: 22, color: summaryStats.unresolvedAlerts > 0 ? 'error.main' : 'text.secondary' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">Açık uyarılar</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {summaryStats.unresolvedAlerts > 0 && (
                  <Chip label="Çözülmemiş" size="small" sx={{ bgcolor: 'error.50', color: 'error.dark', fontWeight: 600, fontSize: '0.7rem' }} />
                )}
                <Tooltip title="Detayları gör">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); navigate('/alerts?status=unresolved'); }}
                    sx={{ color: summaryStats.unresolvedAlerts > 0 ? 'error.main' : 'text.secondary', '&:hover': { bgcolor: 'error.50' } }}
                    aria-label="Uyarı detayları"
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ flex: 1, py: 1.5, px: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
              <Typography variant="caption" color="text.secondary">Çözülmemiş</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, letterSpacing: '-0.02em', color: summaryStats.unresolvedAlerts > 0 ? 'error.main' : 'text.primary' }}>
                {summaryStats.unresolvedAlerts}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                <Chip
                  size="small"
                  label={`eStatement: ${summaryStats.unresolvedEStatementAlerts}`}
                  onClick={(e) => { e.stopPropagation(); navigate('/alerts?status=unresolved&alertTypes=7'); }}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'error.100' } }}
                  aria-label="eStatement uyarılarına git"
                />
                <Chip
                  size="small"
                  label={`Diğer: ${summaryStats.unresolvedNonEStatementAlerts}`}
                  onClick={(e) => { e.stopPropagation(); navigate('/alerts?status=unresolved&alertTypes=1,2,3,4,5,6'); }}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'error.100' } }}
                  aria-label="Diğer uyarılara git"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
        {/* eStatement */}
        <Card
          onClick={() => navigate('/estatement/son-islem-zamani')}
          sx={{
            cursor: 'pointer',
            minHeight: 160,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(236,72,153,0.15)',
            },
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            ...getRefreshCardStyles(isRefreshing),
          }}
        >
          <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : 'secondary.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <InfoIcon sx={{ fontSize: 22, color: 'secondary.main' }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">Son İşlem Zamanı</Typography>
              </Box>
              <Tooltip title="Detayları gör">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); navigate('/estatement/son-islem-zamani'); }}
                  sx={{ color: 'secondary.main', '&:hover': { bgcolor: 'secondary.50' } }}
                  aria-label="Son işlem zamanı detayları"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, py: 1.5, px: 2, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
              <Typography variant="caption" color="text.secondary">Başarılı / Başarısız</Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, letterSpacing: '-0.02em' }}>
                {eStatementBankChartData.reduce((a, b) => a + (b.basarili ?? 0), 0)} <Typography component="span" variant="h5" fontWeight={500} color="text.secondary">/ {eStatementBankChartData.reduce((a, b) => a + (b.basarisiz ?? 0), 0)}</Typography>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Server Grid View – kompakt, light kartlar */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 4,
          bgcolor: 'background.paper',
          ...getRefreshPaperStyles(isRefreshing),
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Sunucular
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {servers.length > 0 ? `${Math.min(12, servers.length)} sunucu` : 'Seçili filtreler için sunucu yok'}
            </Typography>
          </Box>
          {servers.length > 12 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/servers')}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Tümü ({servers.length})
            </Button>
          )}
        </Box>
        
        {servers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            Seçili filtreler için server bulunamadı.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
                xl: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {servers.slice(0, 12).map((server) => {
              const metrics = serverMetrics[server.id];
              // Backend'den gelen server durumunu kullan
              const serverStatus = serverStatuses[server.id];
              const healthStatus = serverStatus?.status || 'unknown';
              const healthStatusReason = serverStatus?.reason;
              const healthStatusReasonDetails = serverStatus?.reasonDetails;
              const appStats = getServerApplicationStats(server.id);
              const serverAgents = agents.filter(a => a.serverId === server.id);
              const hasOnlineAgent = serverStatus?.agentIsOnline ?? (serverAgents.length > 0 && serverAgents.some(a => a.isOnline));
              const isAgentActive = serverAgents.length > 0 && serverAgents.some(a => a.isActive);
              
              // Erişilemeyen sunucu kontrolü
              const isUnreachable = !hasOnlineAgent || 
                healthStatusReason === 'Agent Offline' || 
                healthStatusReason === 'AgentHeartbeat Alert' ||
                healthStatusReason === 'Agent Yok veya Pasif';
              
              // Agent threshold'larını ve özelliklerini al
              const serverAgent = serverAgents[0];
              const hasCpuCap = capabilityHelper.hasCapability(serverAgent?.capabilities, AgentCapability.CpuMonitoring);
              const hasRamCap = capabilityHelper.hasCapability(serverAgent?.capabilities, AgentCapability.MemoryMonitoring);
              const hasDiskCap = capabilityHelper.hasCapability(serverAgent?.capabilities, AgentCapability.DiskMonitoring);
              const cpuWarningThreshold = serverAgent?.cpuWarningThreshold ?? 80;
              const cpuCriticalThreshold = serverAgent?.cpuCriticalThreshold ?? 90;
              const ramWarningThreshold = serverAgent?.ramWarningThreshold ?? 80;
              const ramCriticalThreshold = serverAgent?.ramCriticalThreshold ?? 90;
              const cpuExceedsCritical = hasCpuCap && metrics && metrics.latestCpuUsage >= cpuCriticalThreshold;
              const cpuExceedsWarning = hasCpuCap && metrics && metrics.latestCpuUsage >= cpuWarningThreshold && !cpuExceedsCritical;
              const ramExceedsCritical = hasRamCap && metrics && metrics.latestRamUsagePercent >= ramCriticalThreshold;
              const ramExceedsWarning = hasRamCap && metrics && metrics.latestRamUsagePercent >= ramWarningThreshold && !ramExceedsCritical;

              const getDiskColor = (usageNum: number, diskThresholds: Array<{ drive: string; warning: number; critical: number }> | null, drive: string) => {
                const normalized = drive.trim().toUpperCase().endsWith(':') ? drive.trim().toUpperCase() : drive.trim().toUpperCase() + ':';
                let w = 80, c = 90;
                if (diskThresholds?.length) {
                  const cfg = diskThresholds.find((d: { drive?: string }) => {
                    const n = (d.drive?.trim().toUpperCase() || '').endsWith(':') ? d.drive?.trim().toUpperCase() : (d.drive?.trim().toUpperCase() || '') + ':';
                    return n === normalized;
                  });
                  if (cfg) { w = cfg.warning ?? 80; c = cfg.critical ?? 90; }
                }
                if (usageNum >= c) return 'error.main';
                if (usageNum >= w) return 'warning.main';
                return 'success.main';
              };

              return (
                <Card
                  key={server.id}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 3,
                    border: '1px solid',
                    borderLeft: '4px solid',
                    borderColor: 'divider',
                    borderLeftColor: !hasOnlineAgent && isAgentActive ? 'error.main' : hasOnlineAgent ? 'success.main' : 'grey.400',
                    overflow: 'hidden',
                    position: 'relative',
                    opacity: isUnreachable ? 0.65 : 1,
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? (isUnreachable ? theme.palette.action.hover : theme.palette.background.paper) : (isUnreachable ? theme.palette.grey[50] : theme.palette.background.paper),
                    filter: isUnreachable ? 'grayscale(0.2)' : 'none',
                    boxShadow: hasOnlineAgent
                      ? '0 2px 12px rgba(0,0,0,0.06), -4px 0 12px -2px rgba(16,185,129,0.7), -4px 0 20px -4px rgba(16,185,129,0.4)'
                      : '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      transform: isUnreachable ? 'none' : 'translateY(-3px)',
                      boxShadow: isUnreachable ? 'none' : hasOnlineAgent
                        ? '0 12px 28px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04), -4px 0 14px -2px rgba(16,185,129,0.75), -4px 0 24px -4px rgba(16,185,129,0.45)'
                        : '0 12px 28px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                      borderLeftColor: !hasOnlineAgent && isAgentActive ? 'error.main' : hasOnlineAgent ? 'success.main' : 'grey.500',
                      opacity: isUnreachable ? 0.85 : 1,
                    },
                    ...getRefreshCardStyles(isRefreshing),
                  }}
                  onClick={() => handleServerClick(server.id)}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.25} mb={2}>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isUnreachable ? 'text.disabled' : 'text.primary', letterSpacing: '-0.01em' }}>
                          {server.name}
                        </Typography>
                        <Typography variant="caption" color={isUnreachable ? 'text.disabled' : 'text.secondary'} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {server.tenantName}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip label={hasOnlineAgent ? 'Online' : 'Offline'} color={hasOnlineAgent ? undefined : 'error'} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, ...(hasOnlineAgent && { bgcolor: 'success.50', color: 'success.dark' }) }} />
                        <Tooltip title="Detay">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleServerClick(server.id); }} sx={{ p: 0.35 }} aria-label={`${server.name} detay`}>
                            <ArrowForwardIcon sx={{ fontSize: '1.1rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {metrics ? (
                      <>
                        {/* CPU ve RAM – %50 yan yana */}
                        {(hasCpuCap || hasRamCap) && (
                          <Box sx={{ display: 'flex', gap: 1.25, mb: 2 }}>
                            {hasCpuCap && (
                              <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50], border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress variant="determinate" value={Math.min(100, metrics.latestCpuUsage)} size={48} thickness={4} sx={{ color: cpuExceedsCritical ? 'error.main' : cpuExceedsWarning ? 'warning.main' : 'success.main', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: cpuExceedsCritical ? 'error.main' : cpuExceedsWarning ? 'warning.main' : 'success.main' }}>{metrics.latestCpuUsage.toFixed(0)}%</Typography>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>CPU</Typography>
                              </Box>
                            )}
                            {hasRamCap && (
                              <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50], border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress variant="determinate" value={Math.min(100, metrics.latestRamUsagePercent)} size={48} thickness={4} sx={{ color: ramExceedsCritical ? 'error.main' : ramExceedsWarning ? 'warning.main' : 'success.main', '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.7rem', color: ramExceedsCritical ? 'error.main' : ramExceedsWarning ? 'warning.main' : 'success.main' }}>{metrics.latestRamUsagePercent.toFixed(0)}%</Typography>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>RAM</Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Ayraç: Disk Kullanımı */}
                        {hasDiskCap && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1.5 }} onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ flex: 1, height: 0, borderBottom: '1px solid', borderColor: 'divider' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600, fontSize: '0.75rem' }}>Disk Kullanımı</Typography>
                            <Box sx={{ flex: 1, height: 0, borderBottom: '1px solid', borderColor: 'divider' }} />
                          </Box>
                        )}

                        {/* Disk – carousel: sürüklerken anlık kayma */}
                        {hasDiskCap && (() => {
                          const diskUsageRaw = parseDiskUsage(metrics.latestDiskUsage ?? '{}');
                          const diskUsage = filterDiskUsageByAgentThresholds(diskUsageRaw, serverAgent?.diskThresholds);
                          const diskEntries = Object.entries(diskUsage)
                            .map(([drive, usage]) => ({ drive, usageNum: typeof usage === 'number' ? usage : parseFloat(String(usage)) || 0 }))
                            .sort((a, b) => b.usageNum - a.usageNum);
                          if (diskEntries.length === 0) return null;
                          let diskThresholds: Array<{ drive: string; warning: number; critical: number }> | null = null;
                          try { if (serverAgent?.diskThresholds) diskThresholds = JSON.parse(serverAgent.diskThresholds); } catch { /* ignore */ }
                          const diskPage = diskCarouselIndex[server.id] ?? 0;
                          const diskMaxPage = Math.max(0, Math.ceil(diskEntries.length / carouselPageSize) - 1);
                          const showDiskArrows = diskEntries.length > carouselPageSize;
                          const diskDragOffset = (carouselDragState?.serverId === server.id && carouselDragState?.type === 'disk') ? carouselDragState.dragOffset : 0;
                          const renderDiskSlide = (disks: typeof diskEntries) => (
                            disks.map(({ drive, usageNum }) => {
                              const diskColor = getDiskColor(usageNum, diskThresholds, drive);
                              return (
                                <Box key={drive} sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50], border: '1px solid', borderColor: 'divider' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                                    <CircularProgress variant="determinate" value={Math.min(100, usageNum)} size={42} thickness={4} sx={{ color: diskColor, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.6rem', color: diskColor }}>{usageNum.toFixed(0)}%</Typography>
                                    </Box>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={drive}>{drive}</Typography>
                                </Box>
                              );
                            })
                          );
                          return (
                            <Box sx={{ mb: 1.5, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                              <Box
                                sx={{ overflow: 'hidden', cursor: showDiskArrows ? (carouselDragState?.serverId === server.id && carouselDragState?.type === 'disk' ? 'grabbing' : 'grab') : undefined }}
                                onMouseDown={(e) => handleCarouselMouseDown(server.id, 'disk', diskMaxPage, e)}
                                onWheel={(e) => handleCarouselWheel(server.id, 'disk', diskMaxPage, e)}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1.25,
                                    alignItems: 'stretch',
                                    width: showDiskArrows ? `${(diskMaxPage + 1) * 100}%` : '100%',
                                    transform: showDiskArrows ? `translateX(calc(-${diskPage * 100 / (diskMaxPage + 1)}% + ${diskDragOffset}px))` : 'none',
                                    transition: carouselDragState?.serverId === server.id && carouselDragState?.type === 'disk' ? 'none' : 'transform 0.25s ease-out',
                                  }}
                                >
                                  {showDiskArrows
                                    ? Array.from({ length: diskMaxPage + 1 }).map((_, i) => (
                                        <Box key={i} sx={{ flex: `0 0 ${100 / (diskMaxPage + 1)}%`, display: 'flex', gap: 1.25, minWidth: 0 }}>
                                          {renderDiskSlide(diskEntries.slice(i * carouselPageSize, (i + 1) * carouselPageSize))}
                                        </Box>
                                      ))
                                    : renderDiskSlide(diskEntries)}
                                </Box>
                              </Box>
                              {showDiskArrows && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                                  {Array.from({ length: diskMaxPage + 1 }).map((_, i) => (
                                    <Box key={i} component="button" type="button" onClick={() => setDiskCarouselIndex((p) => ({ ...p, [server.id]: i }))} aria-label={`Disk sayfa ${i + 1}`}
                                      sx={{ width: 6, height: 6, borderRadius: '50%', border: 'none', p: 0, cursor: 'pointer', bgcolor: diskPage === i ? 'primary.main' : 'grey.400', opacity: diskPage === i ? 1 : 0.7, '&:hover': { opacity: 1 } }} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Ayraç: Uygulamalar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1.5 }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ flex: 1, height: 0, borderBottom: '1px solid', borderColor: 'divider' }} />
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600, fontSize: '0.75rem' }}>Uygulamalar</Typography>
                          <Box sx={{ flex: 1, height: 0, borderBottom: '1px solid', borderColor: 'divider' }} />
                        </Box>

                        {/* Uygulamalar – carousel (2 masaüstü / 1 mobil), %50 genişlik */}
                        {(() => {
                          const serverApps = applications.filter((app) => app.serverId === server.id);
                          if (serverApps.length === 0) {
                            return <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Uygulama yok</Typography>;
                          }
                          const appPage = appCarouselIndex[server.id] ?? 0;
                          const appMaxPage = Math.max(0, Math.ceil(serverApps.length / carouselPageSize) - 1);
                          const showAppArrows = serverApps.length > carouselPageSize;
                          const appDragOffset = (carouselDragState?.serverId === server.id && carouselDragState?.type === 'app') ? carouselDragState.dragOffset : 0;
                          const renderAppSlide = (apps: typeof serverApps) => (
                            apps.map((app) => {
                              const status = applicationStatuses[app.id];
                              const lastCheck = status?.latestCheckTime ? formatTimeAgoTR(status.latestCheckTime) : '—';
                              const statusDisplay = getApplicationStatusDisplay(app);
                              const tooltipTitle = [statusDisplay.label, statusDisplay.reason, statusDisplay.reasonDetails].filter(Boolean).join(' — ');
                              const iconColor = statusDisplay.color === 'success' ? 'success.main' : statusDisplay.color === 'error' ? 'error.main' : statusDisplay.color === 'warning' ? 'warning.main' : 'text.secondary';
                              return (
                                <Box key={app.id} sx={{ flex: '1 1 0', minWidth: 0, p: 1.25, borderRadius: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50], border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: statusDisplay.color === 'success' ? 'success.main' : statusDisplay.color === 'error' ? 'error.main' : statusDisplay.color === 'warning' ? 'warning.main' : 'grey.400', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.primary', flex: 1, minWidth: 0 }} title={app.name}>{app.name}</Typography>
                                    <Tooltip title={tooltipTitle} arrow placement="top">
                                      <Box sx={{ flexShrink: 0, display: 'inline-flex', color: iconColor, '& .MuiSvgIcon-root': { fontSize: 18 } }} aria-label={statusDisplay.label}>{statusDisplay.icon}</Box>
                                    </Tooltip>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={app.applicationTypeName}>{app.applicationTypeName}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Son: {lastCheck}</Typography>
                                </Box>
                              );
                            })
                          );
                          return (
                            <Box sx={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                              <Box sx={{ overflow: 'hidden', cursor: showAppArrows ? (carouselDragState?.serverId === server.id && carouselDragState?.type === 'app' ? 'grabbing' : 'grab') : undefined }} onMouseDown={(e) => handleCarouselMouseDown(server.id, 'app', appMaxPage, e)} onWheel={(e) => handleCarouselWheel(server.id, 'app', appMaxPage, e)}>
                                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch', width: showAppArrows ? `${(appMaxPage + 1) * 100}%` : '100%', transform: showAppArrows ? `translateX(calc(-${appPage * 100 / (appMaxPage + 1)}% + ${appDragOffset}px))` : 'none', transition: carouselDragState?.serverId === server.id && carouselDragState?.type === 'app' ? 'none' : 'transform 0.25s ease-out' }}>
                                  {showAppArrows ? Array.from({ length: appMaxPage + 1 }).map((_, i) => (
                                    <Box key={i} sx={{ flex: `0 0 ${100 / (appMaxPage + 1)}%`, display: 'flex', gap: 1.25, minWidth: 0 }}>{renderAppSlide(serverApps.slice(i * carouselPageSize, (i + 1) * carouselPageSize))}</Box>
                                  )) : renderAppSlide(serverApps)}
                                </Box>
                              </Box>
                              {showAppArrows && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                                  {Array.from({ length: appMaxPage + 1 }).map((_, i) => (
                                    <Box key={i} component="button" type="button" onClick={() => setAppCarouselIndex((p) => ({ ...p, [server.id]: i }))} aria-label={`Uygulama sayfa ${i + 1}`} sx={{ width: 6, height: 6, borderRadius: '50%', border: 'none', p: 0, cursor: 'pointer', bgcolor: appPage === i ? 'primary.main' : 'grey.400', opacity: appPage === i ? 1 : 0.7, '&:hover': { opacity: 1 } }} />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Metrik yok
                      </Typography>
                    )}

                    {serverAgent != null && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          v{serverAgent.agentVersion} · {serverAgent.osTypeName} · Son erişim: {serverAgent.lastHeartbeat ? formatDateToTR(serverAgent.lastHeartbeat) : '—'}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Application Status Table - yeni tablo stili */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          bgcolor: 'background.paper',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
          borderRadius: 3,
          overflow: 'hidden',
          ...getRefreshPaperStyles(isRefreshing),
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Uygulama Durumları
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {applicationFilter.status.length === 0
                ? 'Tüm durumlar'
                : `Seçili durumlar: ${applicationFilter.status.map((s) => (s === 'healthy' ? 'Sağlıklı' : s === 'warning' ? 'Uyarı' : 'Kritik')).join(', ')}`}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/application-definitions')}
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Tümünü Gör
          </Button>
        </Box>

        {/* Filtreler */}
        <Box
          display="flex"
          gap={2}
          mb={3}
          flexWrap="wrap"
          sx={{
            '& > *': {
              flex: { xs: '1 1 100%', sm: '1 1 auto', md: '0 1 auto' },
              minWidth: { xs: '100%', sm: '150px', md: '200px' },
            },
          }}
        >
          <TextField
            size="small"
            placeholder="Ara..."
            value={applicationFilter.search}
            onChange={(e) =>
              setApplicationFilter((prev) => ({ ...prev, search: e.target.value }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="application-status-filter-label">Durum</InputLabel>
            <Select
              labelId="application-status-filter-label"
              multiple
              value={applicationFilter.status}
              label="Durum"
              onChange={(e) => {
                const v = e.target.value;
                const next = typeof v === 'string' ? (v === '' ? [] : [v as 'healthy' | 'warning' | 'critical']) : v as Array<'healthy' | 'warning' | 'critical'>;
                setApplicationFilter((prev) => ({ ...prev, status: next }));
              }}
              renderValue={(selected) =>
                selected.length === 0
                  ? 'Tümü'
                  : selected.map((s) => (s === 'healthy' ? 'Sağlıklı' : s === 'warning' ? 'Uyarı' : 'Kritik')).join(', ')
              }
            >
              <MenuItem value="healthy">
                <Checkbox checked={applicationFilter.status.indexOf('healthy') > -1} size="small" />
                <ListItemText primary="Sağlıklı" />
              </MenuItem>
              <MenuItem value="warning">
                <Checkbox checked={applicationFilter.status.indexOf('warning') > -1} size="small" />
                <ListItemText primary="Uyarı" />
              </MenuItem>
              <MenuItem value="critical">
                <Checkbox checked={applicationFilter.status.indexOf('critical') > -1} size="small" />
                <ListItemText primary="Kritik" />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tip</InputLabel>
            <Select
              value={applicationFilter.type}
              label="Tip"
              onChange={(e) =>
                setApplicationFilter((prev) => ({
                  ...prev,
                  type: e.target.value,
                }))
              }
            >
              <MenuItem value="all">Tümü</MenuItem>
              {Array.from(new Set(applications.map((a) => a.applicationTypeName))).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            size="small"
            sx={{ minWidth: 200 }}
            options={[{ id: 'all', name: 'Tümü' }, ...servers]}
            value={
              applicationFilter.server === 'all'
                ? { id: 'all', name: 'Tümü' }
                : servers.find((s) => s.id === applicationFilter.server) ?? { id: 'all', name: 'Tümü' }
            }
            getOptionLabel={(opt) => opt.name ?? ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, newValue) =>
              setApplicationFilter((prev) => ({
                ...prev,
                server: newValue?.id ?? 'all',
              }))
            }
            renderInput={(params) => <TextField {...params} label="Server" placeholder="Ara..." />}
            noOptionsText="Sunucu yok"
          />
        </Box>

        {/* Tablo */}
        <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel
                    active={applicationSort.field === 'serverName'}
                    direction={applicationSort.field === 'serverName' ? applicationSort.direction : 'asc'}
                    onClick={() => handleApplicationSort('serverName')}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Server
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel
                    active={applicationSort.field === 'name'}
                    direction={applicationSort.field === 'name' ? applicationSort.direction : 'asc'}
                    onClick={() => handleApplicationSort('name')}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <AppsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Uygulama
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel
                    active={applicationSort.field === 'status'}
                    direction={applicationSort.field === 'status' ? applicationSort.direction : 'asc'}
                    onClick={() => handleApplicationSort('status')}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <InfoIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Tip
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel
                    active={applicationSort.field === 'status'}
                    direction={applicationSort.field === 'status' ? applicationSort.direction : 'asc'}
                    onClick={() => handleApplicationSort('status')}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <FlagIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Durum
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel
                    active={applicationSort.field === 'lastCheck'}
                    direction={applicationSort.field === 'lastCheck' ? applicationSort.direction : 'asc'}
                    onClick={() => handleApplicationSort('lastCheck')}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Son Kontrol
                    </Box>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Filtrelere uygun uygulama bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApplications.slice(0, 10).map((app, index) => {
                  const statusDisplay = getApplicationStatusDisplay(app);
                  return (
                    <TableRow
                      key={app.id}
                      hover
                      onClick={() => handleApplicationRowClick(app.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.selected' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{app.serverName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <AppsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{app.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Chip label={app.applicationTypeName} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            icon={statusDisplay.icon}
                            label={statusDisplay.label}
                            color={statusDisplay.color}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: '0.75rem', height: 22 }}
                          />
                          {statusDisplay.reasonDetails && (
                            <Tooltip title={statusDisplay.reasonDetails} arrow placement="top">
                              <InfoIcon fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {statusDisplay.time ? formatTimeAgoTR(statusDisplay.time) : '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredApplications.length > 10 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Toplam {filteredApplications.length} uygulama. İlk 10 kayıt gösteriliyor. Tümü için &quot;Tümünü Gör&quot;e tıklayın.
          </Typography>
        )}
      </Paper>

      {/* Son 10 hata (%100 genişlik, modern tablolar) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {/* Kart: Son 10 hata */}
        <Paper
          sx={{
            width: '100%',
            p: 3,
            bgcolor: 'background.paper',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            borderRadius: 3,
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
                Son 10 hata
              </Typography>
              <Typography variant="caption" color="text.secondary">
                eStatement dışı aktif uyarılar
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const includedTypes = [
                  AlertTypeEnum.AgentHeartbeat,
                  AlertTypeEnum.ApplicationUnhealthy,
                  AlertTypeEnum.TaskNotRun,
                ].map((t) => t.toString()).join(',');
                navigate(`/alerts?alertTypes=${includedTypes}&status=${AlertStatus.New}`);
              }}
              endIcon={<ArrowForwardIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Tümünü Gör
            </Button>
          </Box>
          {nonEStatementAlerts.length === 0 ? (
            <Box
              textAlign="center"
              py={5}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50],
              }}
            >
              <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Aktif hata yok
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table
                size="small"
                stickyHeader
                sx={{ '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] } }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Zaman
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <BusinessIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Şirket grubu
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Server
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                        Önem
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <FlagIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Durum
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <MessageIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Mesaj
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', width: 56 }} align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
                        <ReplayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        Tetikle
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nonEStatementAlerts.map((alert, index) => (
                    <TableRow
                      key={alert.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: 'action.selected' },
                        transition: 'background-color 0.15s',
                      }}
                      onClick={() => navigate('/alerts')}
                    >
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {formatTimeAgoTR(alert.createdAt)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {alert.tenantName ?? '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {alert.serverName ?? '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          {alert.severity >= 4 ? <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} /> : <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                          <Chip label={alert.severityName ?? '—'} size="small" sx={{ height: 22, fontSize: '0.75rem' }} color={alert.severity >= 4 ? 'error' : alert.severity >= 3 ? 'warning' : 'default'} variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <FlagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {alert.statusName ?? '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, maxWidth: 240 }} align="left" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Tam mesajı görüntüle">
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.75}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                            onClick={() => { setSelectedMessage(alert.message || null); setMessageDialogOpen(true); }}
                          >
                            <MessageIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            <span>{alert.message || '—'}</span>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, width: 56 }} align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Banka servis tetikleme">
                          <IconButton size="small" color="primary" onClick={() => { setTetiklemeInitialValues(undefined); setTetiklemeDialogOpen(true); }} aria-label="Tetikle">
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Mesaj detay popup */}
      <Dialog open={messageDialogOpen} onClose={() => { setMessageDialogOpen(false); setSelectedMessage(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <span>Mesaj Detayı</span>
          <Tooltip title="Mesajı kopyala">
            <IconButton
              size="small"
              onClick={() => { if (selectedMessage) void navigator.clipboard.writeText(selectedMessage); }}
              disabled={!selectedMessage}
              aria-label="Mesajı kopyala"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {selectedMessage ?? '—'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMessageDialogOpen(false); setSelectedMessage(null); }}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Banka servis tetikleme popup - EHÖ satırından açıldıysa mesajdan parse edilen değerlerle dolu */}
      <TetiklemeDialog
        open={tetiklemeDialogOpen}
        onClose={() => { setTetiklemeDialogOpen(false); setTetiklemeInitialValues(undefined); }}
        initialValues={tetiklemeInitialValues}
      />

        {/* eStatement Chart'ları */}
        <Box mt={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              eStatement Son İşlem Zamanı İstatistikleri
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={3} position="relative">
            {/* Loading Overlay */}
            {loadingEStatementChart && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            
            {/* Banka Bazlı Chart */}
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                opacity: loadingEStatementChart ? 0.6 : 1,
                transition: 'opacity 0.3s ease-in-out',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Banka Bazlı Başarılı/Başarısız Kayıt Sayıları
              </Typography>
              <Box sx={{ width: '100%', height: isMobile ? 320 : 400, minHeight: isMobile ? 280 : 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={eStatementBankChartData}
                    margin={
                      isMobile
                        ? { top: 16, right: 8, left: 0, bottom: 80 }
                        : { top: 20, right: 30, left: 20, bottom: 60 }
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="bankaAdi"
                      angle={isMobile ? -60 : -45}
                      textAnchor="end"
                      height={isMobile ? 72 : 100}
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: isMobile ? 10 : 12 }}
                      tick={{ fill: theme.palette.text.secondary }}
                      interval={isMobile ? 0 : 'preserveStartEnd'}
                      tickMargin={isMobile ? 8 : 12}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: isMobile ? 10 : 12 }}
                      tick={{ fill: theme.palette.text.secondary }}
                      width={isMobile ? 28 : 40}
                      tickFormatter={(v) => (Number(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        color: theme.palette.text.primary,
                        fontSize: isMobile ? 12 : 14,
                      }}
                      cursor={{
                        fill: theme.palette.mode === 'dark' ? theme.palette.action.hover : 'rgba(0, 0, 0, 0.06)',
                        radius: 4,
                      }}
                      wrapperStyle={{ outline: 'none' }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: isMobile ? 11 : 12 }}
                      iconSize={isMobile ? 10 : 12}
                      layout={isMobile ? 'horizontal' : 'horizontal'}
                      verticalAlign="bottom"
                    />
                    <Bar dataKey="basarili" name="Başarılı" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="basarisiz" name="Başarısız" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Son 10 EHÖ Hataları - eStatement İstatistikleri altında */}
            <Paper
              sx={{
                width: '100%',
                p: 3,
                bgcolor: 'background.paper',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
                borderRadius: 3,
                overflow: 'hidden',
                ...getRefreshPaperStyles(isRefreshing),
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>
                    Son 10 EHÖ Hataları
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    eStatement aktif uyarılar
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/alerts?alertTypes=${AlertTypeEnum.EStatement}&status=${AlertStatus.New}`)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Tümünü Gör
                </Button>
              </Box>
              {eStatementAlerts.length === 0 ? (
                <Box
                  textAlign="center"
                  py={5}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50],
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Aktif EHÖ hatası yok
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={{ '& .MuiTableHead-root .MuiTableCell-root': { bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] } }}
                  >
                    <TableHead>
                      <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            Zaman
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            Server
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Şirket
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Banka
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <MessageIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            Mesaj
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                            Önem
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <FlagIcon sx={{ fontSize: 18, color: 'error.main' }} />
                            Durum
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', width: 56 }} align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
                            <ReplayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            Tetikle
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {eStatementAlerts.map((alert, index) => {
                        const parsed = parseEStatementAlertMessage(alert.message || '');
                        const sirketUnvani = alert.serverId && parsed.vkn
                          ? (sirketlerByServerId[alert.serverId]?.find((s) => s.vkn === parsed.vkn)?.unvan ?? '—')
                          : '—';
                        const bankaAdi = parsed.bankCode
                          ? (bankalar.find((b) => String(b.bankaKodu) === parsed.bankCode)?.bankaAdi ?? '—')
                          : '—';
                        return (
                          <TableRow
                            key={alert.id}
                            hover
                            sx={{
                              cursor: 'pointer',
                              bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                              '&:hover': { bgcolor: 'action.selected' },
                              transition: 'background-color 0.15s',
                            }}
                            onClick={() => navigate(`/alerts?alertTypes=${AlertTypeEnum.EStatement}&status=${AlertStatus.New}`)}
                          >
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                {formatTimeAgoTR(alert.createdAt)}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                {alert.serverName ?? '—'}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{sirketUnvani}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>{bankaAdi}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25, maxWidth: 240 }} align="left" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Tam mesajı görüntüle">
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.75}
                                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                                  onClick={() => { setSelectedMessage(alert.message || null); setMessageDialogOpen(true); }}
                                >
                                  <MessageIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                                  <span>{alert.message || '—'}</span>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                {alert.severity >= 4 ? <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} /> : <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                                <Chip label={alert.severityName ?? '—'} size="small" sx={{ height: 22, fontSize: '0.75rem' }} color={alert.severity >= 4 ? 'error' : alert.severity >= 3 ? 'warning' : 'default'} variant="outlined" />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                              <Box display="flex" alignItems="center" gap={0.75}>
                                <FlagIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                {alert.statusName ?? '—'}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', py: 1.25, width: 56 }} align="center" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Banka servis tetikleme">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setTetiklemeInitialValues({
                                      serverId: alert.serverId || undefined,
                                      serviceVkn: parsed.vkn,
                                      bankCode: parsed.bankCode,
                                      uid: parsed.uid,
                                      beginDate: parsed.beginDate,
                                      endDate: parsed.endDate,
                                    });
                                    setTetiklemeDialogOpen(true);
                                  }}
                                  aria-label="Tetikle"
                                >
                                  <ReplayIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        </Box>
    </Layout>
  );
};

export default Dashboard;
